/**
 * Badge - Small status label used to display categorical information.
 * Renders as a colored pill/tag. Variants map to work order statuses
 * (pending, in_progress, completed, overdue) plus a neutral 'default'.
 *
 * This is a low-level atom — for work-order-specific usage with icons and
 * human-readable labels, use the StatusBadge molecule instead.
 *
 * @example
 * <Badge variant="completed">Done</Badge>
 * <Badge variant="overdue">3 days late</Badge>
 */
import React from 'react';

// --- Type definitions ---

/** Status values that correspond to work order lifecycle states */
export type BadgeStatus = 'pending' | 'in_progress' | 'completed' | 'overdue';

/** All available badge variants — statuses plus a neutral 'default' */
export type BadgeVariant = BadgeStatus | 'default';

/**
 * Props for the Badge component.
 */
export interface BadgeProps {
	/** Visual variant controlling background, text, and border color — defaults to 'default' */
	variant?: BadgeVariant;
	/** Badge label content */
	children: React.ReactNode;
	/** Additional CSS classes to merge onto the badge element */
	className?: string;
	/**
	 * Overrides the data-ref marker so a composing molecule (e.g. StatusBadge)
	 * keeps its own atomic identity. Defaults to the atom marker.
	 */
	dataRef?: string;
}

// --- Variant color mappings ---
// Each variant uses a light background, darker text, and a subtle border
// to maintain readability on both white and gray surfaces.
const variantClasses: Record<BadgeVariant, string> = {
	pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
	in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
	completed: 'bg-green-100 text-green-800 border-green-300',
	overdue: 'bg-red-100 text-red-800 border-red-300',
	default: 'bg-gray-100 text-gray-800 border-gray-300',
};

const Badge: React.FC<BadgeProps> = ({ variant = 'default', children, className = '', dataRef = 'atom-badge' }) => {
	// Base classes create a pill shape with consistent typography across all variants
	const baseClasses = 'inline-flex items-center px-10px py-2px rounded-full text-12px font-sans font-medium border';

	return (
		<span className={`badge badge--${variant} ${baseClasses} ${variantClasses[variant]} ${className}`} data-ref={dataRef}>
			{children}
		</span>
	);
};

export default Badge;
